/**
 * =============================================================================
 * IDS CRM Web - SearchSelect
 * =============================================================================
 * Generic type-to-shortlist picker, in two variants.
 *
 * variant="search" (default) is the original: an empty box that finds things
 * you already know the name of. Type at least `minChars`, results arrive from
 * `fetchOptions`, click one to select it, typing again clears the selection.
 * The pattern originated in TicketForm's customer search.
 *
 * variant="combobox" replaces a <select> over a long list. Focusing opens the
 * list straight away with everything in it, so it is browsable without typing
 * — the way the dropdown it replaces was. Typing filters. Because a value is
 * normally already set, typing does NOT clear it: the selection only changes
 * when a row is actually taken, and abandoning a half-typed term restores the
 * label. `fetchOptions` may return a plain array, so a local list needs no
 * network and wants `debounceMs={0}`.
 *
 * Keyboard: arrow keys move the highlight, Enter takes the highlighted row,
 * Escape closes the list. Escape only stops propagating while the list is
 * open, so a closed picker inside a modal still lets Escape reach the modal.
 * In search mode nothing is highlighted until the user asks for one, so Enter
 * keeps submitting the surrounding form the way it always did; a combobox
 * highlights the current selection (or the top row) on open, because there
 * the list is the point.
 *
 * The input is a real ARIA combobox over a listbox. That matters for screen
 * readers, and it means tests reach the input with getByRole('combobox')
 * rather than getByRole('textbox').
 *
 * Three props serve the Parts used picker at the bottom of a phone's Log Work
 * window (spare parts, Push 2). All three default off, so every other caller
 * is unchanged:
 *  - clearOnSelect: the box empties after a pick, ready for the next part.
 *  - inlineResults: the list sits in the page flow (class "inline") instead of
 *    floating, so it can show above the phone keyboard; the box scrolls to the
 *    top of the window when the list opens. Options are 44px, with the
 *    getOptionSub text on its own line.
 *  - keepOpenOnBlur: leaving the box (the iPhone keyboard's Done) keeps the
 *    search and its results. The list still closes on a pick, Escape, a tap
 *    outside or the clear button.
 */

import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import './SearchSelect.css';

function SearchSelect({
  id,
  value,
  initialLabel = '',
  placeholder = 'Type to search...',
  disabled = false,
  minChars,
  debounceMs,
  fetchOptions,
  getOptionId,
  getOptionLabel,
  getOptionSub,
  getOptionGroup,
  onSelect,
  clearable = false,
  selectedIndicator = 'Selected',
  autoFocus = false,
  required = false,
  variant = 'search',
  clearOnSelect = false,
  inlineResults = false,
  keepOpenOnBlur = false
}) {
  const isCombobox = variant === 'combobox';
  const resolvedMinChars = minChars === undefined ? (isCombobox ? 0 : 2) : minChars;
  const resolvedDebounce = debounceMs === undefined ? (isCombobox ? 0 : 300) : debounceMs;

  const [term, setTerm] = useState(initialLabel);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  // Combobox only: has the user typed since focusing? Until they do, the list
  // shows everything rather than filtering by the label already in the box.
  const [typed, setTyped] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const labelRef = useRef(initialLabel);
  labelRef.current = initialLabel;
  // Callers pass these as inline arrows, so they must never reach an effect's
  // dependency list — a new identity every render would refetch forever.
  const idOfRef = useRef(getOptionId);
  idOfRef.current = getOptionId;

  const generatedId = useId();
  const baseId = id || `search-select-${generatedId}`;
  const listboxId = `${baseId}-listbox`;
  const optionDomId = (index) => `${baseId}-option-${index}`;

  // Edit-mode / deep-link seeding: the saved label often arrives after mount.
  // A combobox also follows the label back to empty when the parent clears it.
  useEffect(() => {
    if (initialLabel || isCombobox) setTerm(initialLabel);
  }, [initialLabel, isCombobox]);

  // Where the highlight sits when a result set arrives. Search mode starts with
  // none, so Enter still belongs to the form; a combobox starts on the current
  // selection (or the top row), because there the list is the point.
  const initialHighlight = useCallback((rows) => {
    if (!isCombobox || rows.length === 0) return -1;
    const selected = value ? rows.findIndex(row => idOfRef.current(row) === value) : -1;
    return selected >= 0 ? selected : 0;
  }, [isCombobox, value]);

  const showOptions = useCallback((rows) => {
    setOptions(rows);
    setActiveIndex(initialHighlight(rows));
  }, [initialHighlight]);

  const query = isCombobox && !typed ? '' : term;
  const shouldSearch = !disabled
    && (isCombobox ? open : !value)
    && query.length >= resolvedMinChars;

  useEffect(() => {
    if (!shouldSearch) {
      if (!value || isCombobox) showOptions([]);
      setSearching(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError(false);
      try {
        const rows = await fetchOptions(query);
        if (!cancelled) {
          showOptions(rows || []);
          setOpen(true);
        }
      } catch (err) {
        console.error('SearchSelect fetch failed:', err);
        if (!cancelled) {
          showOptions([]);
          setSearchError(true);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, resolvedDebounce);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, shouldSearch, value, isCombobox, resolvedDebounce, fetchOptions, showOptions]);

  const closeList = useCallback(() => {
    setOpen(false);
    if (isCombobox) {
      // Abandoning a half-typed term must not look like it changed anything.
      setTyped(false);
      setTerm(labelRef.current);
    }
  }, [isCombobox]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeList();
        if (!isCombobox) showOptions([]);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [closeList, isCombobox, showOptions]);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const row = listRef.current.querySelector(`[data-option-index="${activeIndex}"]`);
    // Guarded: jsdom has no scrollIntoView.
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const listOpen = open && options.length > 0 && (isCombobox || !value);

  // An inline list opens under the box: move the box to the top of whatever
  // scrolls (the Log Work window), so the results sit above a phone keyboard.
  useEffect(() => {
    if (!inlineResults || !listOpen || !inputRef.current) return;
    // Guarded: jsdom has no scrollIntoView.
    if (typeof inputRef.current.scrollIntoView === 'function') inputRef.current.scrollIntoView({ block: 'start' });
  }, [inlineResults, listOpen]);

  const handleInput = (event) => {
    setTerm(event.target.value);
    if (isCombobox) {
      setTyped(true);
      setOpen(true);
      return;
    }
    if (value) onSelect(null);
  };

  const handleFocus = (event) => {
    if (!isCombobox || disabled) return;
    // Coming back to a search the blur kept: carry on with it, results and all.
    if (keepOpenOnBlur && typed) {
      setOpen(true);
      // The list never closed, so the effect above does not run again: move
      // the box to the top here, or the keyboard coming back covers the results.
      // Guarded: jsdom has no scrollIntoView.
      if (inlineResults && inputRef.current && typeof inputRef.current.scrollIntoView === 'function') {
        inputRef.current.scrollIntoView({ block: 'start' });
      }
      return;
    }
    setTyped(false);
    setOpen(true);
    // Typing then replaces the label rather than appending to it.
    if (typeof event.target.select === 'function') event.target.select();
  };

  const handlePick = (row) => {
    if (clearOnSelect) {
      // Ready for the next one: nothing for closeList to put back either.
      labelRef.current = '';
      setTerm('');
    } else {
      setTerm(getOptionLabel(row));
    }
    showOptions([]);
    setOpen(false);
    setTyped(false);
    onSelect(row);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!listOpen) {
        // A closed combobox opens on the first arrow, like a <select> does.
        if (isCombobox && !disabled) {
          event.preventDefault();
          setTyped(false);
          setOpen(true);
        }
        return;
      }
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(prev => {
        const next = prev + step;
        if (next < 0) return options.length - 1;
        if (next >= options.length) return 0;
        return next;
      });
      return;
    }
    if (event.key === 'Enter') {
      // No highlight means the user is submitting the form, not picking.
      if (listOpen && activeIndex >= 0) {
        event.preventDefault();
        handlePick(options[activeIndex]);
      }
      return;
    }
    if (event.key === 'Escape' && listOpen) {
      event.preventDefault();
      event.stopPropagation();
      showOptions([]);
      closeList();
    }
  };

  const handleClear = () => {
    setTerm('');
    showOptions([]);
    setOpen(false);
    setTyped(false);
    setSearchError(false);
    onSelect(null);
  };

  let lastGroup = null;

  return (
    <div className="search-select" ref={containerRef}>
      <div className="search-select-input-wrap">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={term}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={isCombobox && !keepOpenOnBlur ? closeList : undefined}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          autoFocus={autoFocus}
          // The box holds a label, never a half-typed term (it is restored on
          // close), so native required-validation on it is meaningful.
          required={required}
          role="combobox"
          aria-expanded={listOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={listOpen && activeIndex >= 0 ? optionDomId(activeIndex) : undefined}
        />
        {clearable && (value || term) && !disabled && (
          <button
            type="button"
            className="search-select-clear"
            onClick={handleClear}
            aria-label="Clear selection"
          >
            &times;
          </button>
        )}
      </div>
      {/* A local combobox filter resolves instantly; a spinner would only flicker. */}
      {searching && !isCombobox && <div className="search-select-status">Searching...</div>}
      {searchError && <div className="search-select-error">Search failed &mdash; try again</div>}
      {listOpen && (
        <div className={`search-select-dropdown${inlineResults ? ' inline' : ''}`} id={listboxId} role="listbox" ref={listRef}>
          {options.map((row, index) => {
            const group = getOptionGroup ? getOptionGroup(row) : null;
            const header = group && group !== lastGroup ? group : null;
            lastGroup = group;
            return (
              <React.Fragment key={getOptionId(row)}>
                {header && <div className="search-select-group" role="presentation">{header}</div>}
                <div
                  id={optionDomId(index)}
                  data-option-index={index}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`search-select-option${index === activeIndex ? ' is-active' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  // Keeps focus in the input, so the blur-to-close never beats the click.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handlePick(row)}
                >
                  <span className="name">{getOptionLabel(row)}</span>
                  {getOptionSub && <span className="number">{getOptionSub(row)}</span>}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
      {value && selectedIndicator && (
        <div className="search-select-indicator">{selectedIndicator}</div>
      )}
    </div>
  );
}

export default SearchSelect;
